import { Injectable } from "@angular/core";
import { Capacitor } from "@capacitor/core";
import type { PluginListenerHandle } from "@capacitor/core";

export type MobileVoiceCaptureStartResult = "started" | "unsupported" | "mic-denied";

type BrowserSpeechRecognition = {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  onresult: ((event: BrowserSpeechRecognitionEvent) => void) | null;
  onerror: ((event: { error: string }) => void) | null;
  onend: (() => void) | null;
  start(): void;
  stop(): void;
  abort(): void;
};

type BrowserSpeechRecognitionEvent = {
  resultIndex: number;
  results: {
    length: number;
    [index: number]: {
      isFinal: boolean;
      [index: number]: { transcript: string };
    };
  };
};

type BrowserSpeechRecognitionCtor = new () => BrowserSpeechRecognition;

type NativeSpeechRecognitionStartOptions = {
  language?: string;
  maxResults?: number;
  partialResults?: boolean;
  popup?: boolean;
};

type NativeSpeechRecognitionPlugin = {
  requestPermissions(): Promise<{ speechRecognition?: string }>;
  available(): Promise<{ available?: boolean }>;
  start(options?: NativeSpeechRecognitionStartOptions): Promise<unknown>;
  stop(): Promise<unknown>;
  getLastPartialResult(): Promise<{ text?: string; matches?: string[] }>;
  forceStop?: () => Promise<void>;
  addListener(
    eventName: "partialResults",
    listener: (event: { accumulatedText?: string; accumulated?: string; matches?: string[] }) => void
  ): Promise<PluginListenerHandle>;
  addListener(
    eventName: "listeningState",
    listener: (event: { status?: string; reason?: string }) => void
  ): Promise<PluginListenerHandle>;
  addListener(eventName: "error", listener: (event: unknown) => void): Promise<PluginListenerHandle>;
};

function resolveBrowserSpeechRecognitionCtor(): BrowserSpeechRecognitionCtor | null {
  if (typeof window === "undefined") {
    return null;
  }
  const w = window as Window & {
    SpeechRecognition?: BrowserSpeechRecognitionCtor;
    webkitSpeechRecognition?: BrowserSpeechRecognitionCtor;
  };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

@Injectable({ providedIn: "root" })
export class MobileVoiceCaptureService {
  private partialListenerHandle: PluginListenerHandle | null = null;
  private nativeListeningHandle: PluginListenerHandle | null = null;
  private nativeErrorListenerHandle: PluginListenerHandle | null = null;
  private browserRecognition: BrowserSpeechRecognition | null = null;
  private browserCommittedTranscript = "";
  private nativeCommittedTranscript = "";
  private nativeListenLanguage = "fr-FR";
  private lastPartialText = "";
  private activeMode: "none" | "browser" | "native" = "none";
  private browserOnPartial: ((text: string) => void) | null = null;
  private nativeOnPartial: ((text: string) => void) | null = null;

  private async loadNativeSpeechRecognition(): Promise<NativeSpeechRecognitionPlugin | null> {
    try {
      const moduleName = "@capgo/capacitor-speech-recognition";
      const mod = (await import(moduleName)) as { SpeechRecognition?: NativeSpeechRecognitionPlugin };
      return mod.SpeechRecognition ?? null;
    } catch {
      return null;
    }
  }

  isNativePlatform(): boolean {
    return Capacitor.isNativePlatform();
  }

  supportsLiveTranscription(): boolean {
    if (this.isNativePlatform()) {
      return true;
    }
    return resolveBrowserSpeechRecognitionCtor() !== null;
  }

  async ensureMicrophoneAccess(): Promise<boolean> {
    if (this.isNativePlatform()) {
      const speechRecognition = await this.loadNativeSpeechRecognition();
      if (!speechRecognition) {
        return false;
      }
      const status = await speechRecognition.requestPermissions();
      return status.speechRecognition === "granted";
    }

    return this.probeMediaDevices();
  }

  async startLiveTranscription(
    onPartial: (text: string) => void,
    language = "fr-FR"
  ): Promise<MobileVoiceCaptureStartResult> {
    await this.stopLiveTranscription();

    if (this.isNativePlatform()) {
      return this.startNativeTranscription(onPartial, language);
    }

    const ctor = resolveBrowserSpeechRecognitionCtor();
    if (!ctor) {
      return "unsupported";
    }

    const micOk = await this.probeMediaDevices();
    if (!micOk) {
      return "mic-denied";
    }

    return this.startBrowserTranscription(ctor, onPartial, language);
  }

  async stopLiveTranscription(): Promise<string> {
    const mode = this.activeMode;
    this.activeMode = "none";

    if (mode === "native") {
      return this.stopNativeTranscription();
    }
    if (mode === "browser") {
      return this.stopBrowserTranscription();
    }

    return this.lastPartialText;
  }

  abortLiveTranscription(): void {
    void this.stopLiveTranscription();
  }

  private async probeMediaDevices(): Promise<boolean> {
    if (typeof navigator === "undefined" || !navigator.mediaDevices?.getUserMedia) {
      return false;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach((track) => track.stop());
      return true;
    } catch {
      return false;
    }
  }

  private async startNativeTranscription(
    onPartial: (text: string) => void,
    language: string
  ): Promise<MobileVoiceCaptureStartResult> {
    try {
      const speechRecognition = await this.loadNativeSpeechRecognition();
      if (!speechRecognition) {
        return "unsupported";
      }

      const status = await speechRecognition.requestPermissions();
      if (status.speechRecognition !== "granted") {
        return "mic-denied";
      }

      const { available } = await speechRecognition.available();
      if (!available) {
        return "unsupported";
      }

      this.lastPartialText = "";
      this.nativeCommittedTranscript = "";
      this.nativeListenLanguage = language.replace("_", "-");
      this.nativeOnPartial = onPartial;

      this.partialListenerHandle = await speechRecognition.addListener("partialResults", (event) => {
        const nextText = this.resolveNativePartialFromEvent(event);
        this.publishNativePartial(nextText, onPartial);
      });

      this.nativeListeningHandle = await speechRecognition.addListener("listeningState", (event) => {
        if (this.activeMode !== "native") {
          return;
        }
        const reason = event.reason ?? "unknown";
        if (event.status !== "stopped" || reason === "userStop" || reason === "forceStop") {
          return;
        }
        this.commitNativeListeningCycle();
        void this.restartNativeListening(speechRecognition);
      });

      this.nativeErrorListenerHandle = await speechRecognition.addListener("error", () => {
        // Keep latest partial text and let stop/cleanup decide final state.
      });

      await speechRecognition.start(this.buildNativeStartOptions(this.nativeListenLanguage));

      this.activeMode = "native";
      return "started";
    } catch {
      await this.cleanupNative();
      return "mic-denied";
    }
  }

  private async stopNativeTranscription(): Promise<string> {
    try {
      const speechRecognition = await this.loadNativeSpeechRecognition();
      if (!speechRecognition) {
        this.nativeOnPartial = null;
        await this.cleanupNative();
        return this.lastPartialText;
      }
      this.commitNativeListeningCycle();
      try {
        await speechRecognition.stop();
      } catch {
        const maybeForceStop = speechRecognition.forceStop;
        if (typeof maybeForceStop === "function") {
          await maybeForceStop();
        }
      }
      try {
        const last = await speechRecognition.getLastPartialResult();
        const pluginFinal =
          this.normalizeTranscript(last.text) || this.normalizeTranscript(last.matches?.[0]);
        if (pluginFinal) {
          this.lastPartialText = this.pickBestNativeTranscript(this.lastPartialText, pluginFinal);
        }
      } catch {
        // ignore missing final payload
      }
    } catch {
      // ignore stop errors
    }

    this.nativeOnPartial = null;
    await this.cleanupNative();
    return this.lastPartialText;
  }

  private async cleanupNative(): Promise<void> {
    if (this.partialListenerHandle) {
      try {
        await this.partialListenerHandle.remove();
      } catch {
        // ignore listener cleanup errors
      }
      this.partialListenerHandle = null;
    }
    if (this.nativeListeningHandle) {
      try {
        await this.nativeListeningHandle.remove();
      } catch {
        // ignore listener cleanup errors
      }
      this.nativeListeningHandle = null;
    }
    if (this.nativeErrorListenerHandle) {
      try {
        await this.nativeErrorListenerHandle.remove();
      } catch {
        // ignore listener cleanup errors
      }
      this.nativeErrorListenerHandle = null;
    }
  }

  private buildNativeStartOptions(language: string): NativeSpeechRecognitionStartOptions {
    return {
      language,
      maxResults: 1,
      partialResults: true,
      popup: false,
    };
  }

  private async restartNativeListening(speechRecognition: {
    start(options?: NativeSpeechRecognitionStartOptions): Promise<unknown>;
  }): Promise<void> {
    if (this.activeMode !== "native") {
      return;
    }
    try {
      await speechRecognition.start(this.buildNativeStartOptions(this.nativeListenLanguage));
    } catch {
      // ignore restart race when stopping
    }
  }

  private commitNativeListeningCycle(): void {
    const current = this.dedupeObviousRepeat(this.lastPartialText);
    if (!current) {
      return;
    }
    if (!this.nativeCommittedTranscript) {
      this.nativeCommittedTranscript = current;
      return;
    }
    if (current === this.nativeCommittedTranscript || this.nativeCommittedTranscript.includes(current)) {
      return;
    }
    if (current.startsWith(this.nativeCommittedTranscript)) {
      this.nativeCommittedTranscript = current;
      return;
    }
    if (current.includes(this.nativeCommittedTranscript)) {
      this.nativeCommittedTranscript = current;
      return;
    }
    this.nativeCommittedTranscript = `${this.nativeCommittedTranscript} ${current}`.trim();
    this.nativeCommittedTranscript = this.dedupeObviousRepeat(this.nativeCommittedTranscript);
  }

  /**
   * Mirror browser SpeechRecognition: matches[0] is the live hypothesis for the current cycle.
   * Plugin accumulated fields are optional and must never be concatenated blindly.
   */
  private resolveNativePartialFromEvent(event: {
    accumulatedText?: string;
    accumulated?: string;
    matches?: string[];
  }): string {
    const match = this.normalizeTranscript(event.matches?.[0]);
    const accumulatedText = this.normalizeTranscript(event.accumulatedText);
    const accumulated = this.normalizeTranscript(event.accumulated);

    let liveHypothesis = match;
    if (!liveHypothesis) {
      liveHypothesis = accumulatedText || accumulated;
    } else if (accumulatedText) {
      liveHypothesis = accumulatedText.length >= liveHypothesis.length ? accumulatedText : liveHypothesis;
    }

    if (!liveHypothesis) {
      return this.lastPartialText;
    }

    if (!this.nativeCommittedTranscript) {
      return liveHypothesis;
    }

    if (liveHypothesis.startsWith(this.nativeCommittedTranscript)) {
      return liveHypothesis;
    }

    if (
      liveHypothesis === this.nativeCommittedTranscript ||
      this.nativeCommittedTranscript.includes(liveHypothesis)
    ) {
      return this.nativeCommittedTranscript;
    }

    return `${this.nativeCommittedTranscript} ${liveHypothesis}`.trim();
  }

  private publishNativePartial(next: string, onPartial: (text: string) => void): void {
    const display = this.dedupeObviousRepeat(this.normalizeTranscript(next));
    if (!display || display === this.lastPartialText) {
      return;
    }
    this.lastPartialText = display;
    onPartial(display);
  }

  private pickBestNativeTranscript(current: string, candidate: string): string {
    const left = this.dedupeObviousRepeat(current);
    const right = this.dedupeObviousRepeat(candidate);
    if (!left) {
      return right;
    }
    if (!right) {
      return left;
    }
    if (right === left) {
      return left;
    }
    if (right.startsWith(left)) {
      return right;
    }
    if (left.startsWith(right)) {
      return left;
    }
    if (this.countRepeatedTail(left) <= this.countRepeatedTail(right)) {
      return left;
    }
    return right;
  }

  private countRepeatedTail(text: string): number {
    const words = text.split(" ");
    for (let size = Math.floor(words.length / 2); size >= 2; size--) {
      const tail = words.slice(-size).join(" ");
      const before = words.slice(-2 * size, -size).join(" ");
      if (tail === before) {
        return size;
      }
    }
    return 0;
  }

  private dedupeObviousRepeat(text: string): string {
    let normalized = this.normalizeTranscript(text);
    if (!normalized) {
      return normalized;
    }

    let changed = true;
    while (changed) {
      changed = false;
      const exactHalf = this.splitExactRepeatedHalves(normalized);
      if (exactHalf && exactHalf !== normalized) {
        normalized = exactHalf;
        changed = true;
        continue;
      }

      const words = normalized.split(" ");
      let deduped = false;
      for (let size = Math.floor(words.length / 2); size >= 2; size--) {
        const tail = words.slice(-size).join(" ");
        const before = words.slice(-2 * size, -size).join(" ");
        if (tail === before) {
          normalized = words.slice(0, -size).join(" ").trim();
          deduped = true;
          break;
        }
      }
      if (deduped) {
        changed = true;
      }
    }

    return normalized;
  }

  private splitExactRepeatedHalves(text: string): string | null {
    for (let index = 1; index < text.length; index++) {
      if (text[index - 1] !== " " && text[index] !== " ") {
        continue;
      }
      const left = text.slice(0, index).trimEnd();
      const right = text.slice(index).trimStart();
      if (left && left === right) {
        return left;
      }
    }
    return null;
  }

  private normalizeTranscript(value: string | undefined): string {
    return (value ?? "").replace(/\s+/g, " ").trim();
  }

  private startBrowserTranscription(
    ctor: BrowserSpeechRecognitionCtor,
    onPartial: (text: string) => void,
    language: string
  ): MobileVoiceCaptureStartResult {
    this.browserCommittedTranscript = "";
    this.lastPartialText = "";
    this.browserOnPartial = onPartial;

    const recognition = new ctor();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = language;

    recognition.onresult = (event: BrowserSpeechRecognitionEvent) => {
      let interim = "";
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        const piece = result[0]?.transcript ?? "";
        if (result.isFinal) {
          this.browserCommittedTranscript += piece;
        } else {
          interim += piece;
        }
      }
      const text = this.browserCommittedTranscript + interim;
      this.lastPartialText = text;
      onPartial(text);
    };

    recognition.onerror = (event: { error: string }) => {
      if (event.error === "not-allowed" || event.error === "service-not-allowed") {
        void this.stopBrowserTranscription();
      }
    };

    recognition.onend = () => {
      if (this.activeMode === "browser") {
        try {
          recognition.start();
        } catch {
          // ignore restart race when stopping
        }
      }
    };

    try {
      recognition.start();
    } catch {
      this.browserRecognition = null;
      this.browserOnPartial = null;
      return "mic-denied";
    }

    this.browserRecognition = recognition;
    this.activeMode = "browser";
    return "started";
  }

  private stopBrowserTranscription(): string {
    const recognition = this.browserRecognition;
    this.browserRecognition = null;
    this.browserOnPartial = null;

    if (recognition) {
      try {
        recognition.onend = null;
        recognition.stop();
      } catch {
        try {
          recognition.abort();
        } catch {
          // ignore
        }
      }
    }

    const text = (this.browserCommittedTranscript || this.lastPartialText).trim();
    this.browserCommittedTranscript = text;
    this.lastPartialText = text;
    return text;
  }
}
